import { isActivePaidSubscription } from './brokerGateVisibility';

/**
 * Whether the caller may use the new-broker mapping controls (broker select,
 * static-IP reservation and Map Broker submit). Presentation only: the backend
 * stays authoritative for mapping and reservation.
 *
 * Operators and Admins keep their existing unconditional access. An ordinary
 * customer (session role `user`) needs a positively loaded, ACTIVE, non-TRIAL,
 * unexpired subscription that belongs to the signed-in account. Loading, failed,
 * missing, foreign or malformed data, and any unknown role, fail closed.
 */
export const BROKER_MAPPING_ELIGIBILITY = Object.freeze({
  ELIGIBLE: 'eligible',
  CHECKING: 'checking',
  VERIFY_FAILED: 'verify_failed',
  PAID_PLAN_REQUIRED: 'paid_plan_required',
});

export const PAID_PLAN_REQUIRED_MESSAGE = 'Broker mapping requires an active paid plan because static IP is required.';
export const CHECKING_SUBSCRIPTION_MESSAGE = 'Checking your subscription…';
export const SUBSCRIPTION_VERIFY_FAILED_MESSAGE = 'We could not verify your subscription, so broker mapping is unavailable right now. Please try again.';

const UNCONDITIONAL_MAPPING_ROLES = new Set(['admin', 'operator']);

export function resolveBrokerMappingEligibility({
  role,
  userId = null,
  subscription,
  isSuccess = false,
  isError = false,
  isFetching = false,
  now = Date.now(),
}) {
  if (UNCONDITIONAL_MAPPING_ROLES.has(role)) return BROKER_MAPPING_ELIGIBILITY.ELIGIBLE;
  if (role !== 'user') return BROKER_MAPPING_ELIGIBILITY.PAID_PLAN_REQUIRED;
  if (isError) {
    return isFetching ? BROKER_MAPPING_ELIGIBILITY.CHECKING : BROKER_MAPPING_ELIGIBILITY.VERIFY_FAILED;
  }
  if (isSuccess !== true || userId == null) return BROKER_MAPPING_ELIGIBILITY.CHECKING;
  // The backend always returns user_id; a missing or different owner can only be
  // stale data from another account, so it never grants mapping.
  if (subscription && String(subscription.user_id ?? '') !== String(userId)) {
    return BROKER_MAPPING_ELIGIBILITY.PAID_PLAN_REQUIRED;
  }
  return isActivePaidSubscription(subscription, { userId, now })
    ? BROKER_MAPPING_ELIGIBILITY.ELIGIBLE
    : BROKER_MAPPING_ELIGIBILITY.PAID_PLAN_REQUIRED;
}

export function brokerMappingIneligibleMessage(eligibility) {
  if (eligibility === BROKER_MAPPING_ELIGIBILITY.CHECKING) return CHECKING_SUBSCRIPTION_MESSAGE;
  if (eligibility === BROKER_MAPPING_ELIGIBILITY.VERIFY_FAILED) return SUBSCRIPTION_VERIFY_FAILED_MESSAGE;
  return PAID_PLAN_REQUIRED_MESSAGE;
}
